import { ClaimsOverTimeChart } from "@/components/charts/claimCharts/ClaimsOverTimeChart";
import { MetricCardsGrid, MetricCard } from "@/components/common/MetricCards";
import { TabContent } from "@/components/common/PageLayout";
import { CompactTable } from "@/components/common/CompactTable";
import { stoplossClaimColumns, reimbursementColumns } from "@/components/common/reportingColumns";
import { ResourceName, ClaimItem, Claimant, Reimbursement, StoplossClaim, Vendor } from "@/types";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { SectionCard } from "@/components/common/SectionCard";
import { Grid2 as Grid, Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useRecordContext, useGetList, useGetMany } from "react-admin";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  IN_PROCESS_STATUSES,
  STOPLOSS_STATUS_GROUP_LABELS,
  SUBMITTED_STATUSES,
} from "@/constants/stoplossClaimsGroupings";
import { getResourceIcon } from "@/utils/icons";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

const ReimbursementIcon = getResourceIcon(ResourceName.REIMBURSEMENTS);
const StopLossClaimsIcon = getResourceIcon(ResourceName.STOPLOSS_CLAIMS);

export const CarrierReportingTab = () => {
  const carrierRecord = useRecordContext<Vendor>();

  const { canList: canListClaims } = useResourcePermissions(ResourceName.STOPLOSS_CLAIMS);
  const { canList: canListReimbursements } = useResourcePermissions(ResourceName.REIMBURSEMENTS);

  const { id: carrierId, is_sl_carrier, is_tpa } = carrierRecord ?? {};

  // Filter key depends on vendor type — carrier uses vendor_id_ref, TPA/Rx use junction table filters
  const filterKey = is_sl_carrier ? "vendor_id_ref" : is_tpa ? "tpa_vendor_id" : "rx_vendor_id";
  const vendorFilter = { [filterKey]: carrierId };

  // Step 1: Get policies for this vendor
  const { data: policies = [], isLoading: policiesLoading } = useGetList(ResourceName.POLICIES, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "status", order: "ASC" },
    filter: vendorFilter,
    meta: { "X-Fields": "id" },
  });

  const policyIdList = useMemo(() => policies.map(({ id }) => id), [policies]);
  const policyFilter = useMemo(() => ({ policy_id_ref: policyIdList }), [policyIdList]);

  // Step 2: Fetch stoploss claims
  const { data: stoplossClaims = [], isLoading: stoplossClaimsLoading } = useGetList<StoplossClaim>(
    ResourceName.STOPLOSS_CLAIMS,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "created_datetime", order: "DESC" },
      filter: policyFilter,
    },
    { enabled: policyIdList.length > 0 }
  );

  const claimIds = useMemo(() => stoplossClaims.map((claim) => claim.id).filter(Boolean), [stoplossClaims]);

  // Fetch claim items for chart
  const { data: claimItems = [], isLoading: claimItemsLoading } = useGetList<ClaimItem>(
    ResourceName.CLAIM_ITEMS,
    {
      pagination: { page: 1, perPage: 10000 },
      filter: { stoploss_claim_id_ref: claimIds },
    },
    { enabled: claimIds.length > 0 }
  );

  // Step 3: Derive client IDs and fetch reimbursements
  const clientIdList = useMemo(
    () => [...new Set(stoplossClaims.map((claim) => claim.client_id_ref).filter(Boolean))],
    [stoplossClaims]
  );
  const clientFilter = useMemo(() => ({ client_id_ref: clientIdList }), [clientIdList]);

  const { data: reimbursements = [], isLoading: reimbursementsLoading } = useGetList<Reimbursement>(
    ResourceName.REIMBURSEMENTS,
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "created_datetime", order: "DESC" },
      filter: clientFilter,
    },
    { enabled: clientIdList.length > 0 }
  );

  // Claimant name enrichment
  const claimantIds = useMemo(() => {
    const ids = new Set<string>();
    stoplossClaims.forEach((c) => c.claimant_id_ref && ids.add(c.claimant_id_ref));
    reimbursements.forEach((r) => r.claimant_id_ref && ids.add(r.claimant_id_ref));
    return Array.from(ids);
  }, [stoplossClaims, reimbursements]);

  const { data: claimants = [] } = useGetMany<Claimant>(
    ResourceName.CLAIMANTS,
    { ids: claimantIds },
    { enabled: claimantIds.length > 0 }
  );

  const claimantMap = useMemo(
    () => claimants.reduce<Record<string, string>>((acc, c) => ({ ...acc, [c.id]: c.claimant_name || "—" }), {}),
    [claimants]
  );

  // Enrich data with claimant names
  const enrichedClaims = useMemo(
    () => stoplossClaims.map((c) => ({ ...c, claimant_name: claimantMap[c.claimant_id_ref] })),
    [stoplossClaims, claimantMap]
  );

  const enrichedReimbursements = useMemo(
    () => reimbursements.map((r) => ({ ...r, claimant_name: claimantMap[r.claimant_id_ref] })),
    [reimbursements, claimantMap]
  );

  // Financial stats
  const financialStats = useMemo(() => {
    const claimTotals = stoplossClaims.reduce(
      (acc, claim) => {
        const amount = claim.total_paid_amount || 0;
        if ((IN_PROCESS_STATUSES as readonly string[]).includes(claim.claim_status)) acc.inProcess += amount;
        if ((SUBMITTED_STATUSES as readonly string[]).includes(claim.claim_status)) acc.submitted += amount;
        return acc;
      },
      { submitted: 0, inProcess: 0 }
    );

    const reimbursementTotals = reimbursements.reduce(
      (acc, reimb: Reimbursement) => {
        acc.pended += Number(reimb.pended_amount) || 0;
        acc.reimbursed += Number(reimb.reimbursement_amount) || 0;
        acc.denied += Number(reimb.denial_amount) || 0;
        return acc;
      },
      { pended: 0, reimbursed: 0, denied: 0 }
    );

    const outstanding =
      claimTotals.submitted -
      (reimbursementTotals.pended + reimbursementTotals.denied + reimbursementTotals.reimbursed);

    return { ...claimTotals, ...reimbursementTotals, outstanding };
  }, [stoplossClaims, reimbursements]);

  // Filter URLs
  const allStoplossClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(JSON.stringify(policyFilter))}`;
  const submittedClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(JSON.stringify({ ...policyFilter, claim_status: SUBMITTED_STATUSES }))}`;
  const inProcessClaimsUrl = `/stoploss-claims?filter=${encodeURIComponent(JSON.stringify({ ...policyFilter, claim_status: IN_PROCESS_STATUSES }))}`;
  const allReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(JSON.stringify(clientFilter))}`;
  const pendedReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(JSON.stringify({ ...clientFilter, pended_amount_gt: 0 }))}`;
  const reimbursedReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(JSON.stringify({ ...clientFilter, reimbursement_amount_gt: 0 }))}`;
  const deniedReimbursementsUrl = `/reimbursements?filter=${encodeURIComponent(JSON.stringify({ ...clientFilter, denial_amount_gt: 0 }))}`;

  const loading = policiesLoading || stoplossClaimsLoading || claimItemsLoading || reimbursementsLoading;

  return (
    <TabContent>
      {/* Financial Stats */}
      <MetricCardsGrid columns={3}>
        <MetricCard
          icon={<StopLossClaimsIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Stop-Loss Claims Activity"
          value={formatUSD(financialStats.submitted + financialStats.inProcess)}
          link={canListClaims ? allStoplossClaimsUrl : undefined}
          subMetrics={[
            {
              label: STOPLOSS_STATUS_GROUP_LABELS.SUBMITTED,
              value: formatUSD(financialStats.submitted),
              link: canListClaims ? submittedClaimsUrl : undefined,
            },
            {
              label: STOPLOSS_STATUS_GROUP_LABELS.IN_PROCESS,
              value: formatUSD(financialStats.inProcess),
              link: canListClaims ? inProcessClaimsUrl : undefined,
            },
          ]}
        />
        <MetricCard
          icon={<ReimbursementIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Reimbursement Activity"
          value={formatUSD(financialStats.pended + financialStats.reimbursed + financialStats.denied)}
          link={canListReimbursements ? allReimbursementsUrl : undefined}
          subMetrics={[
            {
              label: "Pended",
              value: formatUSD(financialStats.pended),
              link: canListReimbursements ? pendedReimbursementsUrl : undefined,
            },
            {
              label: "Reimbursed",
              value: formatUSD(financialStats.reimbursed),
              link: canListReimbursements ? reimbursedReimbursementsUrl : undefined,
            },
            {
              label: "Denied",
              value: formatUSD(financialStats.denied),
              link: canListReimbursements ? deniedReimbursementsUrl : undefined,
            },
          ]}
        />
        <MetricCard
          icon={<AccountBalanceIcon sx={{ fontSize: 18, color: "primary.main" }} />}
          title="Outstanding Amount"
          value={formatUSD(financialStats.outstanding)}
          link={canListClaims ? allStoplossClaimsUrl : undefined}
        />
      </MetricCardsGrid>

      {/* Claims Activity Chart */}
      <Box mt={3}>
        <SectionCard title="Claims Activity Over Time" subtitle="Total paid amounts over time" compact>
          <ClaimsOverTimeChart claimItems={loading ? [] : claimItems} />
        </SectionCard>
      </Box>

      {/* Stop-Loss Claims + Reimbursements */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {canListClaims && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Stop-Loss Claims
            </Typography>
            <CompactTable
              data={enrichedClaims}
              columns={stoplossClaimColumns}
              rowLink={(claim) => `/stoploss-claims/${claim.id}/show`}
              rowsPerPage={10}
              emptyMessage="No stop-loss claims"
            />
          </Grid>
        )}
        {canListReimbursements && (
          <Grid size={{ xs: 12, lg: 6 }}>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Reimbursements
            </Typography>
            <CompactTable
              data={enrichedReimbursements}
              columns={reimbursementColumns}
              rowLink={(reimb) => `/reimbursements/${reimb.id}/show`}
              rowsPerPage={10}
              emptyMessage="No reimbursements"
            />
          </Grid>
        )}
      </Grid>
    </TabContent>
  );
};
